// Family PIN API Service
// Handles all API calls for the "kids as data" Family PIN system

import { Make_HoneyDo_API_Call } from './apiClient';
import {
  FamilyMember,
  FamilyMemberCreationRequest,
  FamilyMemberUpdateRequest,
  FamilyPINValidationRequest,
  FamilyPINValidationResponse,
  FamilyMembersResponse,
  FamilyMemberResponse,
  PINRegenerateResponse
} from '../types/familyMember';

class FamilyPINService {
  private baseURL = '/api/family';

  /**
   * Get all family members for the current family
   * Only accessible to authenticated parents (Cognito users)
   */
  async getFamilyMembers(): Promise<FamilyMembersResponse> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/members`,
        'GET'
      );

      if (response.success) {
        const members: FamilyMember[] = response.data.Records || [];
        // Family PIN is included in response data for parents only
        const responseData: any = response.data;
        const familyPin = responseData.Family_PIN || '';

        return {
          success: true,
          members,
          family_pin: familyPin,
          total_count: members.length
        };
      } else {
        throw new Error(response.message || 'Failed to fetch family members');
      }
    } catch (error) {
      console.error('[FamilyPINService] Error fetching family members:', error);
      throw error;
    }
  }

  /**
   * Create a new family member (kid aged 5-12)
   * Only accessible to authenticated parents
   */
  async createFamilyMember(memberData: FamilyMemberCreationRequest): Promise<FamilyMemberResponse> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/members`,
        'POST',
        memberData
      );

      if (response.success) {
        const member = response.data.Records?.[0] || response.data;

        return {
          success: true,
          member,
          message: `${memberData.member_name} added to your family!`
        };
      } else {
        throw new Error(response.message || 'Failed to create family member');
      }
    } catch (error) {
      console.error('[FamilyPINService] Error creating family member:', error);
      throw error;
    }
  }

  /**
   * Update a family member
   * Only accessible to authenticated parents
   */
  async updateFamilyMember(
    memberId: string,
    updates: FamilyMemberUpdateRequest
  ): Promise<FamilyMemberResponse> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/members/${memberId}`,
        'PUT',
        updates
      );

      if (response.success) {
        const member = response.data.Records?.[0] || response.data;

        return {
          success: true,
          member,
          message: 'Family member updated successfully'
        };
      } else {
        throw new Error(response.message || 'Failed to update family member');
      }
    } catch (error) {
      console.error('[FamilyPINService] Error updating family member:', error);
      throw error;
    }
  }

  /**
   * Delete a family member
   * Only accessible to authenticated parents
   */
  async deleteFamilyMember(memberId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/members/${memberId}`,
        'DELETE'
      );

      return {
        success: response.success,
        message: response.message || 'Family member removed successfully'
      };
    } catch (error) {
      console.error('[FamilyPINService] Error deleting family member:', error);
      throw error;
    }
  }

  /**
   * Validate family PIN and get family members list
   * Used for PIN-based authentication (no Cognito)
   */
  async validatePIN(pin: string): Promise<FamilyPINValidationResponse> {
    try {
      const request: FamilyPINValidationRequest = { pin };

      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/pin/validate`,
        'POST',
        request
      );

      if (response.success) {
        const data = response.data.Records?.[0] || response.data;

        return {
          valid: data.valid,
          family_id: data.family_id,
          members: data.members || [],
          error_message: data.error_message
        };
      } else {
        return {
          valid: false,
          error_message: response.message || 'Invalid PIN'
        };
      }
    } catch (error) {
      console.error('[FamilyPINService] Error validating PIN:', error);
      return {
        valid: false,
        error_message: 'Unable to validate PIN. Please try again.'
      };
    }
  }

  /**
   * Regenerate family PIN
   * Only accessible to authenticated parents
   */
  async regeneratePIN(): Promise<PINRegenerateResponse> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/pin/regenerate`,
        'POST'
      );

      if (response.success) {
        const data = response.data.Records?.[0] || response.data;

        return {
          success: true,
          new_pin: data.new_pin,
          message: 'Family PIN regenerated successfully. Please share the new PIN with your family.'
        };
      } else {
        throw new Error(response.message || 'Failed to regenerate PIN');
      }
    } catch (error) {
      console.error('[FamilyPINService] Error regenerating PIN:', error);
      throw error;
    }
  }

  /**
   * Get tasks assigned to a specific family member
   * Used in kid task view
   */
  async getMemberTasks(memberId: string): Promise<any[]> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `${this.baseURL}/members/${memberId}/tasks`,
        'GET'
      );

      if (response.success) {
        return response.data.Records || [];
      } else {
        throw new Error(response.message || 'Failed to fetch member tasks');
      }
    } catch (error) {
      console.error('[FamilyPINService] Error fetching member tasks:', error);
      return []; // Return empty array instead of throwing for graceful degradation
    }
  }

  /**
   * Mark a task as complete by a family member
   */
  async completeTask(taskId: string, memberId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await Make_HoneyDo_API_Call(
        `/api/tasks/${taskId}/complete`,
        'PUT',
        {
          completed_by_member_id: memberId,
          status: 'completed'
        }
      );

      return {
        success: response.success,
        message: response.message || 'Task marked as complete!'
      };
    } catch (error) {
      console.error('[FamilyPINService] Error completing task:', error);
      throw error;
    }
  }

  /**
   * MOCK: Get family PIN for development/testing
   * In production, this should only be accessible via authenticated parent endpoint
   */
  async _mockGetFamilyPIN(): Promise<string> {
    // This is a mock for frontend development
    // Real implementation will be in backend with proper security
    return '1234';
  }

  /**
   * MOCK: Validate PIN for development/testing
   */
  async _mockValidatePIN(pin: string): Promise<FamilyPINValidationResponse> {
    // Mock data for frontend development
    if (pin === '1234') {
      return {
        valid: true,
        family_id: 'mock-family-123',
        members: [
          {
            member_id: 'mem-1',
            family_id: 'mock-family-123',
            parent_user_id: 'user-123',
            member_name: 'Emma',
            member_age: 8,
            member_type: 'child',
            is_active: true,
            created_at: new Date().toISOString(),
            avatar_emoji: '🌈',
            favorite_color: '#60A5FA'
          },
          {
            member_id: 'mem-2',
            family_id: 'mock-family-123',
            parent_user_id: 'user-123',
            member_name: 'Noah',
            member_age: 10,
            member_type: 'child',
            is_active: true,
            created_at: new Date().toISOString(),
            avatar_emoji: '🚀',
            favorite_color: '#34D399'
          }
        ]
      };
    } else {
      return {
        valid: false,
        error_message: 'Incorrect PIN. Please try again.'
      };
    }
  }
}

export const familyPINService = new FamilyPINService();
